import _ from "lodash";
import { type ReactNode, useMemo, useState } from "react";
import { BuildingIcon, MailIcon, UserIcon } from "lucide-react";
import { GoogleIcon, MicrosoftIcon } from "@/objects/@icons";

import {
  EmailConnectionStatusEnum,
  EmailConnectionVisibilityEnum,
  ExternalProviderEnum,
} from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { NodeTabLayout } from "@/components/organisms/Node/NodeTabLayout";
import { Mutations, Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useContextScope } from "@/hooks/useContextTree";
import { useScope } from "@/hooks/useScope";
import {
  MAILBOX_PROVIDERS,
  PLACEHOLDER_MAILBOX,
  type Mailbox,
  useConnectMailbox,
} from "@/app/app/_/commands/ConnectMailboxCommand";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { SettingsViewCard, SettingsViewLayout } from "./SettingsViewLayout";

import { Checkbox } from "@/components/ui/checkbox";

type BusinessOption = {
  identifier: string;
  label: string;
  isPersonal: boolean;
};

/** The page's two ways of looking at the same links: each mailbox's businesses, or each business's mailboxes. */
const EMAILS_VIEWS = [
  { id: "email", label: "By email" },
  { id: "entity", label: "By entity" },
];

/** A business drawn while the businesses load, holding the placeholder mailbox. */
const PLACEHOLDER_BUSINESS: BusinessOption = {
  identifier: "placeholder",
  label: "Business",
  isPersonal: false,
};

/**
 * A person's mailboxes and the businesses each is given to, seen either way
 * round: by email, each mailbox with its businesses; by entity, each business
 * with the mailboxes searched for it. A mailbox is connected once, lands in its
 * owner's personal books, and can be given to any of their businesses - in each
 * it is theirs alone until they share it there. Teammates' mailboxes shared in
 * the viewer's businesses are listed read-only.
 */
export function SettingsEmailsView() {
  const { entities } = useScope();
  const { Scope } = useContextScope();
  const [view, setView] = useState("email");

  // Every business the viewer belongs to, their own books included.
  const businessOptions = useMemo(
    (): BusinessOption[] =>
      (entities ?? []).flatMap((entity) =>
        entity
          ? [
              {
                identifier: entity.identifier,
                label: entity.isUserEntity
                  ? "Personal"
                  : (entity.name ?? entity.identifier),
                isPersonal: entity.isUserEntity,
              },
            ]
          : [],
      ),
    [entities],
  );
  const { data, loading } = useBackendQuery(Queries.EMAIL_CONNECTIONS_QUERY, {
    variables: {
      entityIdentifiers: businessOptions.map((option) => option.identifier),
    },
    skip: !entities,
    fetchPolicy: "cache-and-network",
  });
  const mailboxes: Mailbox[] = useMemo(
    () => data?.emailConnections?.data ?? [],
    [data],
  );
  const isLoading = (!entities || loading) && mailboxes.length === 0;
  const mine = isLoading
    ? [PLACEHOLDER_MAILBOX]
    : mailboxes.filter((mailbox) => mailbox.ownedByViewer);
  const theirs = mailboxes.filter((mailbox) => !mailbox.ownedByViewer);
  // One of theirs its provider signed out: its sign-in made ready, as each
  // provider's is.
  const signedOut = mailboxes.find(
    (mailbox) =>
      mailbox.ownedByViewer &&
      mailbox.status !== EmailConnectionStatusEnum.Connected,
  );
  const providerKeyOf = (mailbox: Mailbox) =>
    MAILBOX_PROVIDERS.find((provider) => provider.provider === mailbox.provider)
      ?.providerConfigKey ?? null;
  const signedOutKey = signedOut ? providerKeyOf(signedOut) : null;
  const { connect, connectingProvider } = useConnectMailbox(
    signedOut && signedOutKey
      ? { identifier: signedOut.identifier, providerConfigKey: signedOutKey }
      : null,
  );
  const reconnect = (mailbox: Mailbox) => {
    const key = providerKeyOf(mailbox);
    if (!key || connectingProvider !== null) return;
    void connect(key, {
      connected: mine.map((one) => one.identifier),
      reconnectIdentifier: mailbox.identifier,
    });
  };

  // Only the owner may give or share a mailbox; anyone else is told so. What
  // it is given to comes back on the mailbox itself, so nothing is refetched.
  const { mutate: assignEmailAccount } = useBackendMutation({
    mutation: Mutations.ASSIGN_EMAIL_ACCOUNT,
  });
  const { mutate: shareEmailAccount } = useBackendMutation({
    mutation: Mutations.SHARE_EMAIL_ACCOUNT,
    refetchQueries: ["EmailConnections"],
  });
  // A tick shows at once, from either view, and each is sent with every
  // business ticked so far for that mailbox - none waits on the one before;
  // one refused falls back to what is saved.
  const [ticked, setTicked] = useState<Record<string, string[]>>({});
  const givenTo = (mailbox: Mailbox) =>
    ticked[mailbox.identifier] ??
    mailbox.assignments.map((assignment) => assignment.entity.identifier);
  const toggleGiven = (mailbox: Mailbox, business: string) => {
    const current = givenTo(mailbox);
    const entityIdentifiers = current.includes(business)
      ? current.filter((identifier) => identifier !== business)
      : [...current, business];
    setTicked((all) => ({ ...all, [mailbox.identifier]: entityIdentifiers }));
    assignEmailAccount({
      variables: {
        input: { identifier: mailbox.identifier, entityIdentifiers },
      },
      callbacks: {
        onError: () => setTicked((all) => _.omit(all, mailbox.identifier)),
      },
    });
  };
  const share = (
    mailbox: Mailbox,
    business: string,
    visibility: EmailConnectionVisibilityEnum,
  ) =>
    shareEmailAccount({
      variables: {
        input: {
          identifier: mailbox.identifier,
          entityIdentifier: business,
          visibility,
        },
      },
    });

  return (
    <SettingsViewLayout
      title="Emails"
      endDecorator={
        <div className="flex gap-2">
          {MAILBOX_PROVIDERS.map((provider) => (
            <Button
              variant="outline"
              size="sm"
              key={provider.providerConfigKey}
              disabled={
                connectingProvider === provider.providerConfigKey ||
                (connectingProvider !== null &&
                  connectingProvider !== provider.providerConfigKey)
              }
              onClick={() =>
                void connect(provider.providerConfigKey, {
                  connected: mine.map((mailbox) => mailbox.identifier),
                })
              }
            >
              {connectingProvider === provider.providerConfigKey && <Spinner />}
              Connect {provider.title}
            </Button>
          ))}
        </div>
      }
    >
      <Scope>
        <div className="flex-1 min-h-0 flex flex-col">
          {/* On the title bar's surface, as a record's tabs are under its title. */}
          <div className="shrink-0 bg-background">
            <NodeTabLayout
              groups={EMAILS_VIEWS}
              activeGroupId={view}
              setActiveGroupId={setView}
              size="md"
            />
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            {view === "email" ? (
              <div
                data-testid="settings-emails-mailboxes"
                className="flex flex-col gap-3 p-4"
              >
                {mine.length === 0 && (
                  <SettingsViewCard
                    icon={MailIcon}
                    title="No mailbox connected"
                    description="Connect Gmail or Outlook, and it is searched for the receipt behind a transaction."
                  />
                )}
                {/* Theirs first, then the ones teammates shared with them. */}
                {[...mine, ...theirs].map((mailbox) => (
                  <SettingsEmailsMailbox
                    key={mailbox.id}
                    mailbox={mailbox}
                    isLoading={isLoading}
                    onReconnect={() => reconnect(mailbox)}
                  >
                    {mailbox.ownedByViewer &&
                      (isLoading
                        ? [PLACEHOLDER_BUSINESS]
                        : businessOptions
                      ).map((business) => (
                        <SettingsEmailsGivenRow
                          key={business.identifier}
                          label={business.label}
                          mailbox={mailbox}
                          business={business}
                          isGiven={givenTo(mailbox).includes(
                            business.identifier,
                          )}
                          isLoading={isLoading}
                          onToggle={() =>
                            toggleGiven(mailbox, business.identifier)
                          }
                          onShare={(visibility) =>
                            share(mailbox, business.identifier, visibility)
                          }
                          data-testid={`settings-emails-given-${business.identifier}`}
                        />
                      ))}
                  </SettingsEmailsMailbox>
                ))}
              </div>
            ) : (
              <div
                data-testid="settings-emails-businesses"
                className="flex flex-col gap-3 p-4"
              >
                {(isLoading ? [PLACEHOLDER_BUSINESS] : businessOptions).map(
                  (business) => {
                    const searched = mine.filter((mailbox) =>
                      givenTo(mailbox).includes(business.identifier),
                    ).length;
                    const sharedHere = theirs.filter((mailbox) =>
                      mailbox.assignments.some(
                        (assignment) =>
                          assignment.entity.identifier === business.identifier,
                      ),
                    );
                    return (
                      <SettingsViewCard
                        key={business.identifier}
                        data-testid={`settings-emails-business-${business.identifier}`}
                        icon={business.isPersonal ? UserIcon : BuildingIcon}
                        title={business.label}
                        description={
                          mine.length === 0
                            ? "No mailbox connected yet."
                            : searched === 0
                              ? "None of your mailboxes is searched for its receipts."
                              : `${searched} of your ${mine.length === 1 ? "mailbox" : "mailboxes"} searched for its receipts.`
                        }
                        isLoading={isLoading}
                      >
                        {mine.map((mailbox) => (
                          <SettingsEmailsGivenRow
                            key={mailbox.id}
                            label={mailbox.emailAddress}
                            mailbox={mailbox}
                            business={business}
                            isGiven={givenTo(mailbox).includes(
                              business.identifier,
                            )}
                            isLoading={isLoading}
                            onToggle={() =>
                              toggleGiven(mailbox, business.identifier)
                            }
                            onShare={(visibility) =>
                              share(mailbox, business.identifier, visibility)
                            }
                            data-testid={`settings-emails-business-${business.identifier}-${mailbox.identifier}`}
                          />
                        ))}
                        {sharedHere.map((mailbox) => (
                          <div
                            key={mailbox.id}
                            className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border"
                          >
                            <div className="flex text-muted-foreground/80">
                              <Icon ReactIcon={MailIcon} size="md" />
                            </div>
                            <Typography
                              level="body-md"
                              className="truncate flex-1 min-w-0"
                            >
                              {mailbox.emailAddress}
                            </Typography>
                            <Typography
                              level="body-sm"
                              className="truncate text-muted-foreground/80"
                            >
                              Shared by {mailbox.ownerName}
                            </Typography>
                          </div>
                        ))}
                      </SettingsViewCard>
                    );
                  },
                )}
              </div>
            )}
          </div>
        </div>
      </Scope>
    </SettingsViewLayout>
  );
}

/**
 * One mailbox: its address and when it was last searched, then - for its
 * owner - its rows (a business each, from the page); a teammate's names who
 * shared it and where.
 */
function SettingsEmailsMailbox({
  mailbox,
  isLoading,
  onReconnect,
  children,
}: {
  mailbox: Mailbox;
  isLoading: boolean;
  /** Its owner signs in to it again, one its provider signed out. */
  onReconnect?: () => void;
  children?: ReactNode;
}) {
  // A disconnect refused says why, rather than leaving the mailbox as it was
  // without a word.
  const { mutate: disconnectEmailAccount, loading: isDisconnecting } =
    useBackendMutation({
      mutation: Mutations.DISCONNECT_EMAIL_ACCOUNT,
      refetchQueries: ["EmailConnections"],
    });

  return (
    <SettingsViewCard
      data-testid={`settings-emails-mailbox-${mailbox.identifier}`}
      icon={
        mailbox.provider === ExternalProviderEnum.Outlook
          ? MicrosoftIcon
          : GoogleIcon
      }
      title={mailbox.emailAddress}
      description={
        mailbox.ownedByViewer
          ? mailbox.lastSearchedAt
            ? `Last searched ${new Date(mailbox.lastSearchedAt).toLocaleString()}`
            : "Not searched yet"
          : `Shared by ${mailbox.ownerName} in ${mailbox.assignments
              .map((assignment) => assignment.entity.name)
              .join(", ")}`
      }
      isLoading={isLoading}
      actions={
        <>
          {mailbox.status !== EmailConnectionStatusEnum.Connected && (
            <SimpleEnum
              label="Reconnect needed"
              enumColor={EnumColors.ORANGE}
            />
          )}
          {mailbox.ownedByViewer &&
            mailbox.status !== EmailConnectionStatusEnum.Connected && (
              <Button
                variant="outline"
                size="sm"
                data-testid="settings-emails-reconnect"
                onClick={onReconnect}
              >
                Reconnect
              </Button>
            )}
          {mailbox.ownedByViewer && (
            <Skeleton
              loading={isLoading}
              variant="rectangular"
              className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (
                    !window.confirm(
                      `Disconnect ${mailbox.emailAddress}? Receipts already imported stay.`,
                    )
                  ) {
                    return;
                  }
                  disconnectEmailAccount({
                    variables: { input: { identifier: mailbox.identifier } },
                  });
                }}
                disabled={isDisconnecting}
                className="text-destructive hover:text-destructive"
              >
                {isDisconnecting && <Spinner />}Disconnect
              </Button>
            </Skeleton>
          )}
        </>
      }
    >
      {children}
    </SettingsViewCard>
  );
}

/**
 * One of the owner's mailboxes in one business, from whichever side the page
 * shows it: ticked where it is searched for the business's receipts, with who
 * searches it there - and, while hovered, what that means in plain words.
 */
function SettingsEmailsGivenRow({
  label,
  mailbox,
  business,
  isGiven,
  isLoading,
  onToggle,
  onShare,
  "data-testid": dataTestid,
}: {
  /** What the row names: the business, or the mailbox. */
  label: string;
  mailbox: Mailbox;
  business: BusinessOption;
  isGiven: boolean;
  isLoading: boolean;
  onToggle: () => void;
  onShare: (visibility: EmailConnectionVisibilityEnum) => void;
  "data-testid"?: string;
}) {
  const assignment = mailbox.assignments.find(
    (given) => given.entity.identifier === business.identifier,
  );
  // What the row means, in plain words, shown while it is hovered.
  const hint = !isGiven
    ? business.isPersonal
      ? "Not searched for your personal receipts."
      : `Not searched for ${business.label}'s receipts.`
    : business.isPersonal
      ? "It is searched for receipts in your personal books."
      : assignment?.visibility === EmailConnectionVisibilityEnum.Members
        ? `Everyone in ${business.label} can search it for matching receipts.`
        : `Private to you in ${business.label}. Teammates' searches can spot a matching receipt here without seeing your email, and you decide whether to share it.`;

  return (
    <div
      data-testid={dataTestid}
      className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border [&:hover_[data-hint]]:opacity-[1] [&:focus-within_[data-hint]]:opacity-[1]"
    >
      <Skeleton
        loading={isLoading}
        variant="rectangular"
        className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
      >
        <Checkbox
          checked={isGiven}
          onCheckedChange={() => onToggle()}
          aria-label={`Search ${mailbox.emailAddress} for ${business.label}`}
        />
      </Skeleton>
      <Typography
        level="body-md"
        className={cn(
          "truncate",
          "shrink-0 max-w-[40%]",
          isGiven ? "text-foreground" : "text-muted-foreground/80",
        )}
      >
        <Skeleton loading={isLoading} variant="inline">
          {label}
        </Skeleton>
      </Typography>
      <Typography
        level="body-sm"
        data-hint
        className="truncate flex-1 min-w-0 text-muted-foreground/80 opacity-[0]"
      >
        {isLoading ? null : hint}
      </Typography>
      {isGiven && (
        <Skeleton
          loading={isLoading}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
        >
          {/* In the owner's own books there is no one to share it with;
              just ticked, it is theirs alone until it is saved. */}
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            disabled={business.isPersonal || !assignment}
            value={
              assignment?.visibility ?? EmailConnectionVisibilityEnum.Private
            }
            onValueChange={(visibility) => {
              if (!visibility || visibility === assignment?.visibility) {
                return;
              }
              onShare(visibility as EmailConnectionVisibilityEnum);
            }}
            aria-label={`Who searches it in ${business.label}`}
            className="bg-card"
          >
            <ToggleGroupItem value={EmailConnectionVisibilityEnum.Private}>
              Only me
            </ToggleGroupItem>
            <ToggleGroupItem value={EmailConnectionVisibilityEnum.Members}>
              All members
            </ToggleGroupItem>
          </ToggleGroup>
        </Skeleton>
      )}
    </div>
  );
}
